import React, { useState } from 'react'
import { DashboardLayout, Navigation } from '@toolpad/core'

import { ReactRouterAppProvider } from '@toolpad/core/react-router' // Import the ReactRouterAppProvider
import { useNavigate } from 'react-router'

import { Stack, Typography, Menu, MenuItem, Divider, Avatar } from '@mui/material'
import { createTheme } from '@mui/material/styles'
import { backgroundColors, brandColors, textColors } from '../Color/Color'
import { useAppSelector } from '../../../redux/store'
import { selectUser, selectUserRole } from '../../../redux/auth/selector'

import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded'
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded'
import DonutSmallRoundedIcon from '@mui/icons-material/DonutSmallRounded'
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import GradingRoundedIcon from '@mui/icons-material/GradingRounded'
import LOGO from '../../../assets/images/Logo.png'

import { fontSizes } from '../Typography/TypographyStyles'
import { Box } from '@mui/system'

import { theme } from '../../../theme/ThemeProvider'
import PageContent from './PageContent'
import FloatingButton from '../FloatingButton/FloatingButton'

const customTheme = createTheme({
  ...theme,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: backgroundColors.purpleGradient, // AppBar background
          color: backgroundColors.white, // AppBar text/icon color
          '& .MuiIconButton-root': {
            color: backgroundColors.white, // AppBar icon color
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          display: 'flex',
          flexDirection: 'column',
          // height: '100vh',
          color: textColors.purpleTelus,
          '& .MuiList-root': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },

          '& .MuiListItemButton-root': {
            color: textColors.purpleTelus,
            '&:hover': {
              backgroundColor: brandColors.greenTusk,
              color: textColors.purpleTelus,
            },
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
})

function getNavigation(role: string): Navigation {
  const isAdminOrManager = role === 'admin' || role === 'manager'

  const baseNav: Navigation = [
    {
      kind: 'header',
      title: 'DASHBOARD',
    },
    {
      segment: 'overview',
      title: 'OVERVIEW',
      icon: <DashboardRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />,
    },
    {
      segment: 'events',
      title: 'EVENTS',
      icon: <EventRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />,
      ...(isAdminOrManager
        ? {
            children: [
              {
                segment: '',
                title: 'VIEW EVENTS',
                icon: (
                  <CircleOutlinedIcon
                    sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }}
                  />
                ),
              },
              {
                segment: 'manage-events',
                title: 'MANAGE EVENTS',
                icon: (
                  <CircleOutlinedIcon
                    sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }}
                  />
                ),
              },
            ],
          }
        : {}),
    },
    {
      segment: 'tasks',
      title: 'TASKS',
      icon: (
        <FormatListBulletedRoundedIcon
          sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }}
        />
      ),
      ...(isAdminOrManager
        ? {
            children: [
              {
                segment: '',
                title: 'VIEW TASKS',
                icon: (
                  <CircleOutlinedIcon
                    sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }}
                  />
                ),
              },
              {
                segment: 'manage-tasks',
                title: 'MANAGE TASKS',
                icon: (
                  <CircleOutlinedIcon
                    sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }}
                  />
                ),
              },
            ],
          }
        : {}),
    },
    {
      segment: 'orders',
      title: 'ORDERS',
      icon: <LocalMallRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />,
    },
  ]

  if (role === 'admin') {
    baseNav.push(
      {
        kind: 'divider',
      },
      {
        kind: 'header',
        title: 'ADMINISTRATION',
      },
      {
        segment: 'team-roster',
        title: 'ROSTER',
        icon: <GroupsRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />,
      },
      {
        segment: 'expenses',
        title: 'EXPENSES',
        icon: (
          <AccountBalanceRoundedIcon
            sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }}
          />
        ),
      },
      {
        segment: 'reports',
        title: 'REPORTS',
        icon: (
          <DonutSmallRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />
        ),
        children: [
          {
            segment: 'event',
            title: 'EVENTS',
            icon: (
              <CircleOutlinedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }} />
            ),
          },
          {
            segment: 'task',
            title: 'TASKS',
            icon: (
              <CircleOutlinedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }} />
            ),
          },
        ],
      },
    )
  }

  if (role === 'manager') {
    baseNav.push(
      {
        kind: 'divider',
      },
      {
        kind: 'header',
        title: 'REPORTS',
      },
      {
        segment: 'reports',
        title: 'REPORTS',
        icon: (
          <DonutSmallRoundedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size20 }} />
        ),
        children: [
          {
            segment: 'event',
            title: 'EVENTS',
            icon: (
              <CircleOutlinedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }} />
            ),
          },
          {
            segment: 'task',
            title: 'TASKS',
            icon: (
              <CircleOutlinedIcon sx={{ color: textColors.purpleTelus, fontSize: fontSizes.size14 }} />
            ),
          },
        ],
      },
    )
  }

  // Removed navigation items for User Guide and Help & Support

  return baseNav
}

function CustomAppTitle() {
  const navigate = useNavigate()
  return (
    <Stack direction='row' alignItems='center' spacing={2}>
      <Box
        component='img'
        src={LOGO}
        alt='logo'
        height={50}
        alignItems='center'
        display='flex'
        onClick={() => {
          navigate('/')
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      />
    </Stack>
  )
}

function ToolbarUser() {
  const authUser = useAppSelector(selectUser)
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [avatarImgError, setAvatarImgError] = useState(false)
  const [menuImgError, setMenuImgError] = useState(false)

  const pictureUrl = authUser?.googleUserInfo?.picture
  const firstName = authUser?.teamMemberInfo?.firstName ?? ''
  const lastName = authUser?.teamMemberInfo?.lastName ?? ''
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSettingsClick = () => {
    navigate('/settings')
    handleClose()
  }

  const handleUserGuideClick = () => {
    navigate('/user-guide')
    handleClose()
  }

  const handleFeedbackClick = () => {
    navigate('/support')
    handleClose()
  }

  return (
    <>
      {pictureUrl && !avatarImgError ? (
        <Box
          component='img'
          src={pictureUrl}
          alt='user'
          height={40}
          width={40}
          onClick={handleClick}
          onError={() => setAvatarImgError(true)}
          referrerPolicy='no-referrer'
          data-testid='user-avatar-button'
          sx={{
            borderRadius: '50%',
            objectFit: 'cover',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        />
      ) : (
        <Avatar
          onClick={handleClick}
          data-testid='user-avatar-button'
          sx={{
            width: 40,
            height: 40,
            cursor: 'pointer',
            bgcolor: brandColors.greenTusk,
            color: textColors.purpleTelus,
            fontWeight: 'bold',
            fontSize: fontSizes.size14,
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          {initials || 'U'}
        </Avatar>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 2,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: 1,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 15,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuItem
          sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
          onClick={handleSettingsClick}
          data-testid='user-profile-menu-item'
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            {pictureUrl && !menuImgError ? (
              <Box
                component='img'
                src={pictureUrl}
                alt='user'
                height={45}
                width={45}
                onError={() => setMenuImgError(true)}
                referrerPolicy='no-referrer'
                sx={{
                  borderRadius: '50%',
                  objectFit: 'cover',
                  cursor: 'pointer',
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 45,
                  height: 45,
                  bgcolor: brandColors.greenTusk,
                  color: textColors.purpleTelus,
                  fontWeight: 'bold',
                  fontSize: fontSizes.size14,
                }}
              >
                {initials || 'U'}
              </Avatar>
            )}
            <Stack>
              <Typography sx={{ fontWeight: 'bold', fontSize: fontSizes.size14 }}>
                {firstName} {lastName}
              </Typography>
              <Typography variant='body2' sx={{ opacity: 0.7 }}>
                {authUser?.googleUserInfo?.email}
              </Typography>
              <Typography variant='body2' sx={{ opacity: 0.7 }}>
                {authUser?.teamMemberInfo?.jobProfile}
              </Typography>
            </Stack>
          </Stack>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleUserGuideClick} data-testid='user-guide-menu-item'>
          <Stack direction='row' spacing={1} alignItems='center'>
            <MenuBookRoundedIcon fontSize='small' />
            <Typography>User Guide</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={handleFeedbackClick} data-testid='feedback-menu-item'>
          <Stack direction='row' spacing={1} alignItems='center'>
            <GradingRoundedIcon fontSize='small' />
            <Typography>Feedback</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </>
  )
}

interface DashboardProps {
  children: React.ReactNode
}

const Dashboard: React.FC<DashboardProps> = ({ children }) => {
  const userRole = useAppSelector(selectUserRole)
  const navigation = getNavigation(userRole)

  return (
    <ReactRouterAppProvider navigation={navigation} theme={customTheme}>
      <DashboardLayout
        slots={{
          appTitle: CustomAppTitle,
          toolbarAccount: ToolbarUser,
        }}
        sidebarExpandedWidth={250}
      >
        <PageContent components={children} />
        <FloatingButton />
      </DashboardLayout>
    </ReactRouterAppProvider>
  )
}

export default Dashboard
